import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { getAccessToken } from "@/lib/storeManager";
import type { MyPermission } from "@/utils/types/tenantPermissions";
import type { AdminPermissionKey } from "@/project/karuna/constants/adminPermissions";
import {
  canManageAdminUsers,
  fetchAndStoreAdminPermissions,
  getStoredAdminPermissions,
  hasAdminPermission,
  hasAnyAdminPermission,
  hasLegacyFullAccess,
} from "@/project/karuna/utils/adminPermissionUtils";

interface AdminPermissionsContextValue {
  permissions: MyPermission[];
  isLegacyFullAccess: boolean;
  refreshPermissions: () => Promise<MyPermission[]>;
  hasPermission: (key: AdminPermissionKey) => boolean;
  hasAnyPermission: () => boolean;
  canManageUsers: () => boolean;
}

const AdminPermissionsContext = createContext<
  AdminPermissionsContextValue | undefined
>(undefined);

export function AdminPermissionsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [permissions, setPermissions] = useState<MyPermission[]>(
    () => getStoredAdminPermissions() as MyPermission[]
  );
  const isRefreshingRef = useRef(false);

  const refreshPermissions = useCallback(async () => {
    if (isRefreshingRef.current) {
      return getStoredAdminPermissions() as MyPermission[];
    }
    isRefreshingRef.current = true;
    try {
      const next = await fetchAndStoreAdminPermissions();
      setPermissions(next);
      return next;
    } finally {
      isRefreshingRef.current = false;
    }
  }, []);

  // Refresh once when the admin shell mounts — silent, no loading flash.
  useEffect(() => {
    if (!getAccessToken()) {
      return;
    }
    void refreshPermissions();
  }, [refreshPermissions]);

  const value = useMemo<AdminPermissionsContextValue>(
    () => ({
      permissions,
      isLegacyFullAccess: hasLegacyFullAccess(permissions),
      refreshPermissions,
      hasPermission: (key: AdminPermissionKey) =>
        hasAdminPermission(permissions, key),
      hasAnyPermission: () => hasAnyAdminPermission(permissions),
      canManageUsers: () => canManageAdminUsers(permissions),
    }),
    [permissions, refreshPermissions]
  );

  return (
    <AdminPermissionsContext.Provider value={value}>
      {children}
    </AdminPermissionsContext.Provider>
  );
}

export function useAdminPermissions() {
  const context = useContext(AdminPermissionsContext);
  if (!context) {
    throw new Error(
      "useAdminPermissions must be used within AdminPermissionsProvider"
    );
  }
  return context;
}
